import * as dt from 'constants/draft_types'

import { createSelector } from 'reselect'

import { selectAgentsById } from 'selectors/agents/base'
import { selectGroupsById, selectPreviousMailboxId } from 'selectors/app'
import {
  selectCurrentUser,
  selectPreferences,
  selectPreferredMailboxId,
} from 'ducks/currentUser/selectors'
import { selectAttachmentsByTicketId } from 'selectors/attachments/base'
import { selectCustomersById } from 'ducks/customers/selectors'
import { selectLabelsById } from 'selectors/labels/base'
import {
  selectFirstMailboxId,
  selectMailboxIdFromUrl,
} from 'selectors/mailboxes_base'
import { selectCurrentMailboxId } from 'selectors/mailboxes'
import { selectIsForwarding } from 'selectors/page'

import { stripTemplateTags } from 'util/cannedReply'
import {
  ticketHasAttachments,
  getFirstOrEmptyFromDraftRecipient,
} from 'util/draft'
import { isEmpty as isEmptyObject } from 'util/objects'
import {
  stripHTML,
  isHtmlEmpty,
  isValidEmail,
  withoutUnprintable,
} from 'util/strings'

import { selectCurrentTicket } from 'selectors/tickets/current'
import { selectTicket } from 'selectors/tickets/byId'
import { selectIsTwitterTicket } from 'selectors/tickets/type'
import { selectFrozenDraft } from './frozenDraft'
import { selectDefaultNote, selectPreferredReassignment } from './replyPrefs'
import { selectUndecoratedTicketReplyDrafts } from './replyDraftsBase'
import { createReplyDraftBuilder, getPlainBodyOrFrozen } from '../util'

// DEPRECATED. Where possible, put logic into our _PAGE reducers instead.
const selectReplyDraftPropsFromPrefs = createSelector(
  selectPreferences,
  selectPreferredReassignment,
  selectIsForwarding,
  selectCurrentTicket,
  (preferences, reassignment, isForwarding, ticket) => {
    const replyDraftPropsFromPrefs = {
      state:
        isForwarding && ticket ? ticket.state : preferences.default_reply_state,
    }

    if (!isForwarding && preferences.reassign_ticket_on_reply) {
      Object.assign(replyDraftPropsFromPrefs, reassignment)
    }

    return replyDraftPropsFromPrefs
  }
)

const selectReplyDraftBuilder = createSelector(
  selectUndecoratedTicketReplyDrafts,
  selectAgentsById,
  selectGroupsById,
  selectCurrentUser,
  selectDefaultNote,
  selectReplyDraftPropsFromPrefs,
  selectFirstMailboxId,
  selectPreviousMailboxId,
  selectPreferredMailboxId,
  selectCurrentMailboxId,
  selectCustomersById,
  selectMailboxIdFromUrl,
  selectLabelsById,
  (
    drafts,
    agentsById,
    groupsById,
    currentUser,
    defaultNote,
    replyDraftPropsFromPrefs,
    firstMailboxId,
    previousMailboxId,
    preferredMailboxId,
    currentMailboxId,
    customersById,
    mailboxIdFromUrl,
    labelsById
  ) => {
    return ticket => {
      return createReplyDraftBuilder(
        drafts,
        ticket,
        agentsById,
        groupsById,
        currentUser,
        defaultNote,
        replyDraftPropsFromPrefs,
        firstMailboxId,
        previousMailboxId,
        preferredMailboxId,
        currentMailboxId,
        customersById,
        mailboxIdFromUrl,
        labelsById
      )
    }
  }
)

export const selectReplyDraft = createSelector(
  selectCurrentTicket,
  selectReplyDraftBuilder,
  (ticket, builder) => {
    return builder(ticket)
  }
)

export const selectReplyDraftForTicketId = createSelector(
  selectTicket,
  selectReplyDraftBuilder,
  (ticket, builder) => {
    if (ticket) {
      return builder(ticket)
    }
    return null
  }
)

export const makeSelectTicketReplyDraftBodyPlainText = ticketId => {
  return createSelector(
    selectUndecoratedTicketReplyDrafts,
    selectFrozenDraft,
    getPlainBodyOrFrozen(ticketId, true)
  )
}

export const makeSelectDraftIsForwardedForTicketList = ticketId => {
  return createSelector(
    selectUndecoratedTicketReplyDrafts,
    undecoratedReplyDrafts => {
      return (undecoratedReplyDrafts[ticketId] || {}).type === dt.FORWARD
    }
  )
}

export const selectDraftIsForwarded = createSelector(
  selectReplyDraft,
  draft => draft.isForwarding
)

export const selectTicketDraftId = createSelector(
  selectReplyDraft,
  draft => draft.id
)

const isEmptyBodyContent = (body, stripTwitterUsername = false) => {
  let test = stripHTML(body)
  if (stripTwitterUsername) {
    test = test.replace(/^@\w+\s*\n*$/, '')
  }
  return withoutUnprintable(test).length === 0
}

const selectIsTwitterReplyWithNoContent = createSelector(
  selectCurrentTicket,
  selectReplyDraft,
  (ticket, draft) => {
    if (ticket && ticket.isTwitterTicket) {
      return isEmptyBodyContent(draft.body, ticket.isTwitterTicket) // no content
    }
    return false
  }
)

const selectIsAttachingToReplyDraft = createSelector(
  selectAttachmentsByTicketId,
  selectReplyDraft,
  (byId, draft) => byId && byId[draft.id] && !isEmptyObject(byId[draft.id])
)

export const selectTicketDraftIsSendable = createSelector(
  selectReplyDraft,
  selectIsTwitterTicket,
  selectIsTwitterReplyWithNoContent,
  selectIsForwarding,
  selectIsAttachingToReplyDraft,
  (
    draft,
    isTwitterTicket,
    isTwitterReplyWithNoContent,
    isForwarding,
    isAttaching
  ) => {
    const to = getFirstOrEmptyFromDraftRecipient('to', draft)
    if (!draft) return false
    if (!draft.to) return false
    if (draft.to.length === 0) return false
    if (!isTwitterTicket && !to.email) return false // could be a reply to a logged conversation...
    if (isTwitterReplyWithNoContent) return false
    if (isAttaching) return false
    if (!isHtmlEmpty(draft.body)) return true
    if (ticketHasAttachments(draft)) return true
    if (isForwarding) return true
    return false
  }
)

export const selectNewEmailIsSendable = createSelector(
  selectReplyDraft,
  selectIsAttachingToReplyDraft,
  (draft, isAttaching) => {
    const to = getFirstOrEmptyFromDraftRecipient('to', draft)
    if (!draft) return false
    if (!draft.to) return false
    if (draft.to.length === 0) return false
    if (!to.email || !isValidEmail(to.email)) return false
    if (isAttaching) return false
    if (!isHtmlEmpty(draft.body)) return true
    if (ticketHasAttachments(draft)) return true
    return false
  }
)

export const selectReplyDraftPreflight = createSelector(
  selectReplyDraft,
  draft => ({
    ...draft,
    body: stripTemplateTags(draft.body),
    title: stripTemplateTags(draft.title),
  })
)
